import React, { Fragment, useRef, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faSpinner, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import type { ImportProgressData } from '../../utils/sseUpload';

export interface ImportDialogProps {
  open: boolean;
  title: string;
  accept?: string;
  onClose: () => void;
  /**
   * Called when the user clicks "Start Import".
   * The optional `onProgress` callback can be invoked by the service layer
   * to push real-time SSE progress events into the dialog's progress bar.
   */
  onImport: (
    file: File,
    onProgress?: (data: ImportProgressData) => void,
  ) => Promise<{ success: boolean; message: string }>;
  onComplete?: () => void;
}

const PHASE_LABELS: Record<string, string> = {
  'building-map': 'Building lookup map...',
  importing: 'Importing rows...',
  finalizing: 'Finalizing...',
};

/**
 * Reusable CSV import dialog with real-time progress bar.
 * Flow: pick file → click Start → progress bar → result summary → Close.
 */
const ImportDialog: React.FC<ImportDialogProps> = ({
  open,
  title,
  accept = '.csv',
  onClose,
  onImport,
  onComplete,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null);
  const [progress, setProgress] = useState<ImportProgressData | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0] ?? null;
    setFile(f);
    setResult(null);
    setProgress(null);
  };

  const handleStart = async () => {
    if (!file) return;
    setImporting(true);
    setResult(null);
    setProgress(null);
    try {
      const res = await onImport(file, (data) => setProgress(data));
      setResult(res);
    } catch {
      setResult({ success: false, message: 'An unexpected error occurred.' });
    } finally {
      setImporting(false);
      setProgress(null);
    }
  };

  const handleClose = () => {
    if (importing) return; // prevent closing during import
    if (result?.success) onComplete?.();
    setFile(null);
    setResult(null);
    setProgress(null);
    if (fileRef.current) fileRef.current.value = '';
    onClose();
  };

  const pct = progress && progress.total > 0
    ? Math.min(Math.round((progress.processed / progress.total) * 100), 100)
    : 0;

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-30" onClose={handleClose}>
        {open && (
          <>
            <Transition.Child
              as="div"
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
              <div className="flex min-h-full items-center justify-center p-4">
                <Transition.Child
                  as="div"
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Dialog.Panel className="relative w-full max-w-md transform overflow-hidden rounded-lg bg-white shadow-xl transition-all">
                    {/* Header */}
                    <div className="bg-gray-50 px-6 py-4 border-b">
                      <Dialog.Title className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                        <FontAwesomeIcon icon={faFileImport} className="text-gray-500" />
                        {title}
                      </Dialog.Title>
                    </div>

                    {/* Body */}
                    <div className="px-6 py-5">
                      {importing ? (
                        <div className="flex flex-col gap-4 py-4">
                          {/* Phase label */}
                          <div className="flex items-center gap-2">
                            <FontAwesomeIcon icon={faSpinner} spin className="text-lg text-storyblue" />
                            <p className="text-sm font-medium text-gray-700">
                              {progress
                                ? (PHASE_LABELS[progress.phase] || progress.phase)
                                : 'Starting import...'}
                            </p>
                          </div>

                          {/* Progress bar */}
                          {progress && (
                            <>
                              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                                <div
                                  className="bg-storyblue h-3 rounded-full transition-all duration-300 ease-out"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>

                              <div className="flex justify-between text-xs text-gray-500">
                                <span>
                                  {progress.processed.toLocaleString()} / {progress.total.toLocaleString()} rows
                                </span>
                                <span className="font-semibold text-gray-700">{pct}%</span>
                              </div>

                              {/* Live counters */}
                              <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="bg-green-50 rounded-md px-2 py-1.5">
                                  <p className="text-xs text-green-600 font-medium">Created</p>
                                  <p className="text-sm font-bold text-green-700">{progress.created.toLocaleString()}</p>
                                </div>
                                <div className="bg-blue-50 rounded-md px-2 py-1.5">
                                  <p className="text-xs text-blue-600 font-medium">Updated</p>
                                  <p className="text-sm font-bold text-blue-700">{progress.updated.toLocaleString()}</p>
                                </div>
                                <div className="bg-gray-50 rounded-md px-2 py-1.5">
                                  <p className="text-xs text-gray-500 font-medium">Skipped</p>
                                  <p className="text-sm font-bold text-gray-600">{progress.skipped.toLocaleString()}</p>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      ) : result ? (
                        <div className="flex flex-col items-center gap-3 py-4">
                          <FontAwesomeIcon
                            icon={result.success ? faCheckCircle : faTimesCircle}
                            className={`text-4xl ${result.success ? 'text-green-500' : 'text-red-500'}`}
                          />
                          <p className="text-sm text-gray-700 text-center whitespace-pre-line">
                            {result.message}
                          </p>
                        </div>
                      ) : (
                        <div className="flex flex-col gap-3">
                          <p className="text-sm text-gray-600">Select a CSV file to import.</p>
                          <input
                            ref={fileRef}
                            type="file"
                            accept={accept}
                            onChange={handleFileChange}
                            className="block w-full text-sm text-gray-700 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-storyblue file:text-white hover:file:opacity-90 cursor-pointer"
                          />
                          {file && (
                            <p className="text-xs text-gray-500">
                              Selected: <span className="font-medium">{file.name}</span>{' '}
                              ({(file.size / 1024).toFixed(1)} KB)
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="bg-gray-50 px-6 py-3 flex justify-end gap-2 border-t">
                      {!importing && !result && (
                        <>
                          <button
                            onClick={handleClose}
                            className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleStart}
                            disabled={!file}
                            className="px-4 py-2 rounded-md text-sm font-medium text-white bg-storyblue hover:opacity-90 disabled:opacity-50"
                          >
                            Start Import
                          </button>
                        </>
                      )}
                      {!importing && result && (
                        <button
                          onClick={handleClose}
                          className="px-4 py-2 rounded-md text-sm font-medium text-white bg-storyblue hover:opacity-90"
                        >
                          Close
                        </button>
                      )}
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </>
        )}
      </Dialog>
    </Transition.Root>
  );
};

export default ImportDialog;
